import React, { useState, useEffect } from 'react';
import { X, Percent, DollarSign, AlertCircle, RefreshCw } from 'lucide-react';
import { useCreateDiscount, useUpdateDiscount } from '../../hooks/useDiscounts';
import type { DiscountConfig, DiscountType, CreateDiscountData } from '@/types/tuition.types';

interface Props {
  discount?: DiscountConfig | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DISCOUNT_TYPES: { value: DiscountType; label: string; icon: string }[] = [
  { value: 'SIBLING', label: 'Fratrie', icon: '👨‍👩‍👧‍👦' },
  { value: 'MERIT', label: 'Mérite', icon: '🏆' },
  { value: 'PARTNER', label: 'Partenaire', icon: '🤝' },
  { value: 'SOCIAL', label: 'Social', icon: '❤️' },
  { value: 'EARLY_PAYMENT', label: 'Paiement Anticipé', icon: '⏰' },
  { value: 'STAFF_CHILD', label: 'Enfant du Personnel', icon: '👨‍💼' },
  { value: 'AMBASSADOR', label: 'Ambassadeur', icon: '🌟' },
  { value: 'OTHER', label: 'Autre', icon: '📋' },
];

const DiscountFormModal: React.FC<Props> = ({ discount, onClose, onSuccess }) => {
  const isEditing = !!discount;
  const createMutation = useCreateDiscount();
  const updateMutation = useUpdateDiscount();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'OTHER' as DiscountType,
    isPercentage: true,
    value: 0,
    maxDeductionAmount: undefined as number | undefined,
    description: '',
    isRecurring: true,
    requiresApproval: false,
    isCumulative: false,
    priority: 0,
  });

  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (discount) {
      setFormData({
        name: discount.name,
        code: discount.code,
        type: discount.type,
        isPercentage: discount.percentageValue !== null,
        value: discount.percentageValue ?? discount.fixedAmount ?? 0,
        maxDeductionAmount: discount.maxDeductionAmount ?? undefined,
        description: discount.description || '',
        isRecurring: discount.isRecurring,
        requiresApproval: discount.requiresApproval,
        isCumulative: discount.isCumulative,
        priority: discount.priority,
      });
    }
  }, [discount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Le nom est obligatoire');
      return;
    }
    if (formData.value <= 0) {
      setError('La valeur doit être supérieure à 0');
      return;
    }
    if (formData.isPercentage && formData.value > 100) {
      setError('Le pourcentage ne peut pas dépasser 100%');
      return;
    }

    try {
      const data: CreateDiscountData = {
        name: formData.name.trim(),
        code: formData.code.trim() || undefined,
        type: formData.type,
        isPercentage: formData.isPercentage,
        value: formData.value,
        maxDeductionAmount:
          formData.isPercentage && formData.maxDeductionAmount
            ? formData.maxDeductionAmount
            : undefined,
        description: formData.description.trim() || undefined,
        isRecurring: formData.isRecurring,
        requiresApproval: formData.requiresApproval,
        isCumulative: formData.isCumulative,
        priority: formData.priority,
      };

      if (isEditing && discount) {
        const res: any = await updateMutation.mutateAsync({ id: discount.id, data });
        const updatedCount = res?.studentsUpdated ?? 0;
        if (updatedCount > 0) {
          // Let the user know the change propagated before closing.
          setInfo(
            `Remise mise à jour. ${updatedCount} étudiant(s) concerné(s) ont été recalculés (solde et échéances mis à jour).`,
          );
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 2600);
          return;
        }
      } else {
        await createMutation.mutateAsync(data);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Modifier la remise' : 'Nouvelle remise'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {info && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <AlertCircle size={18} />
              {info}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Ex: Remise Fratrie"
            />
          </div>

          {/* Code (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code (optionnel)
            </label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Auto-généré si vide"
              disabled={isEditing}
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {DISCOUNT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, type: type.value })}
                  className={`flex flex-col items-center p-2 rounded-lg border-2 transition-colors ${
                    formData.type === type.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-xl mb-1">{type.icon}</span>
                  <span className="text-xs text-gray-600 text-center">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Value Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de valeur <span className="text-red-500">*</span>
            </label>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isPercentage: true })}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium transition-colors ${
                  formData.isPercentage
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Percent size={16} />
                Pourcentage
              </button>
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, isPercentage: false, maxDeductionAmount: undefined })
                }
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium transition-colors ${
                  !formData.isPercentage
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <DollarSign size={16} />
                Montant fixe (MAD)
              </button>
            </div>
          </div>

          {/* Value */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {formData.isPercentage ? 'Pourcentage' : 'Montant (MAD)'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                min="0"
                max={formData.isPercentage ? 100 : undefined}
                step={formData.isPercentage ? 0.1 : 1}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {formData.isPercentage ? '%' : 'MAD'}
              </span>
            </div>
          </div>

          {/* Max Deduction (only for percentage) */}
          {formData.isPercentage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Montant maximum (MAD) - optionnel
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.maxDeductionAmount || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxDeductionAmount: e.target.value ? parseFloat(e.target.value) : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  min="0"
                  step="1"
                  placeholder="Sans limite"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">MAD</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Si le pourcentage dépasse ce montant, seul ce montant sera déduit
              </p>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optionnel)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={2}
              placeholder="Description de la remise..."
            />
          </div>

          {/* Options */}
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Récurrent</span>
                <p className="text-xs text-gray-500">
                  Appliqué automatiquement chaque année académique
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requiresApproval}
                onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Approbation requise</span>
                <p className="text-xs text-gray-500">
                  Nécessite une validation avant application
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isCumulative}
                onChange={(e) => setFormData({ ...formData, isCumulative: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Cumulable</span>
                <p className="text-xs text-gray-500">Peut être combiné avec d'autres remises</p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <RefreshCw size={16} className="animate-spin" />}
              {isEditing ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DiscountFormModal;
